import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  QuestionAnswer as QuestionIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { paaAnalysisService } from '../../services/paaAnalysisService';

interface ContentGap {
  question_id: string;
  question_text: string;
  gap_type: string;
  gap_importance: 'high' | 'medium' | 'low';
  suggested_content: string;
  is_answered_in_content: boolean;
}

interface AnalysisResult {
  summary: {
    total_questions: number;
    answered: number;
    unanswered: number;
    critical_gaps: string[];
  };
  questions: Array<{
    question_id: number;
    question: string;
    is_answered: boolean;
    answer_quote: string | null;
    gap_type: string;
    importance: string;
    suggested_content: string;
  }>;
}

interface PAAContentAnalysisProps {
  urlId: string;
  url: string;
  questionCount: number;
  onAnalysisComplete?: () => void;
}

const PAAContentAnalysis: React.FC<PAAContentAnalysisProps> = ({
  urlId,
  url,
  questionCount,
  onAnalysisComplete,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await paaAnalysisService.analyzeSingle(urlId);
      
      if (result.success && result.data) {
        setAnalysis(result.data);
        onAnalysisComplete?.();
      } else {
        setError(result.error || 'Analysis failed');
      }
    } catch (err: any) {
      setError('Failed to analyze content');
    } finally {
      setLoading(false);
    }
  };

  const getImportanceColor = (importance: string) => {
    switch (importance) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getGapTypeLabel = (gapType: string) => {
    const labels: Record<string, string> = {
      pricing: '💰 Pricing',
      hours: '🕐 Hours',
      location: '📍 Location',
      accessibility: '♿ Accessibility',
      amenities: '🏢 Amenities',
      visit_info: 'ℹ️ Visit Info',
      general: '📝 General',
    };
    return labels[gapType] || gapType;
  };

  return (
    <>
      <Tooltip title="Analyze which PAA questions are answered in your content">
        <Button
          variant="outlined"
          size="small"
          startIcon={<AnalyticsIcon />}
          onClick={() => setOpen(true)}
          sx={{ ml: 1 }}
        >
          Content Analysis
        </Button>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">PAA Content Gap Analysis</Typography>
            {analysis && (
              <IconButton onClick={handleAnalyze} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {url}
          </Typography>
        </DialogTitle>

        <DialogContent>
          {!analysis && !loading && (
            <Box textAlign="center" py={4}>
              <QuestionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Analyze {questionCount} PAA Questions
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Discover which questions are answered in your content and identify gaps
              </Typography>
              <Button
                variant="contained"
                startIcon={<AnalyticsIcon />}
                onClick={handleAnalyze}
                size="large"
              >
                Start Analysis
              </Button>
            </Box>
          )}

          {loading && (
            <Box textAlign="center" py={4}>
              <CircularProgress size={64} sx={{ mb: 2 }} />
              <Typography variant="h6">Analyzing Content...</Typography>
              <Typography variant="body2" color="text.secondary">
                Checking {questionCount} questions against your page content
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {analysis && (
            <>
              {/* Summary Stats */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Total Questions
                      </Typography>
                      <Typography variant="h4">
                        {analysis.summary.total_questions}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Answered
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {analysis.summary.answered}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(analysis.summary.answered / analysis.summary.total_questions) * 100}
                        sx={{ mt: 1 }}
                        color="success"
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        Content Gaps
                      </Typography>
                      <Typography variant="h4" color="error.main">
                        {analysis.summary.unanswered}
                      </Typography>
                      {analysis.summary.critical_gaps.length > 0 && (
                        <Chip
                          label={`${analysis.summary.critical_gaps.length} Critical`}
                          color="error"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Question Analysis */}
              <Typography variant="h6" gutterBottom>
                Question Analysis
              </Typography>
              <List>
                {analysis.questions.map((q, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box display="flex" alignItems="center" width="100%" mb={1}>
                      <ListItemIcon>
                        {q.is_answered ? (
                          <CheckIcon color="success" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={q.question}
                        secondary={
                          q.is_answered && q.answer_quote
                            ? `✓ "${q.answer_quote}"`
                            : null
                        }
                      />
                    </Box>
                    
                    {!q.is_answered && (
                      <Box width="100%" pl={7}>
                        <Box display="flex" gap={1} mb={1}>
                          <Chip
                            label={getGapTypeLabel(q.gap_type)}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={q.importance}
                            size="small"
                            color={getImportanceColor(q.importance)}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Suggested content:</strong> {q.suggested_content}
                        </Typography>
                      </Box>
                    )}
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          {analysis && (
            <Button
              variant="contained"
              startIcon={<WarningIcon />}
              color="warning"
            >
              Export Gaps ({analysis.summary.unanswered})
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PAAContentAnalysis;